'use client';

import { useMemo, useState } from 'react';
import type { SiteRendererProps } from '@locusly/client-runtime';
import heroPng from './assets/wedding-table-hero.png';
import productsPng from './assets/decor-products.png';
import defaultContent from './defaultContent';
import type { BlogLocalizedContent, LocuslyBlogContent } from './content.schema';

type ImportedAsset = string | { src: string };

type BlogPostSection =
  | { type: 'p'; text: string }
  | { type: 'lead'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'quote'; text: string; cite?: string }
  | { type: 'callout'; title: string; text: string }
  | { type: 'divider' };

type BlogPost = {
  slug: string;
  locale: 'pt' | 'en';
  title: string;
  description: string;
  publishedAt: string;
  updatedAt: string;
  author: { name: string; role?: string };
  tags: string[];
  readingTimeMin: number;
  coverEmoji: string;
  sections: BlogPostSection[];
  translationKey?: string;
};

type BlogRuntimeData = {
  locale?: 'pt' | 'en';
  posts?: BlogPost[];
  post?: BlogPost | null;
  catalogItems?: CatalogItem[];
};

type DisplayPost = {
  title: string;
  description: string;
  date: string;
  href: string;
  tag?: string;
};

type Product = {
  title: string;
  price: string;
  source: string;
  crop: string;
  href?: string;
  imageUrl?: string;
  category?: string;
  description?: string;
  isBudgetPick?: boolean;
  isFeatured?: boolean;
};

type CatalogItem = {
  slug: string;
  kind: 'product' | 'look' | 'guide';
  locale: 'pt' | 'en';
  title: string;
  description: string;
  category: string;
  imageUrl?: string;
  imageAlt?: string;
  tags: string[];
  sortOrder: number;
  price?: string;
  source?: string;
  affiliateUrl?: string;
  isBudgetPick?: boolean;
  isFeatured?: boolean;
  palette?: string[];
  productSlugs?: string[];
  stylingTips?: string[];
  steps?: string[];
  budgetEstimate?: string;
  polishedEstimate?: string;
};

type GuideCard = {
  number: string;
  title: string;
  description: string;
  href: string;
  crop: string;
  imageUrl?: string;
  steps?: string[];
};

type LookCard = {
  number: string;
  title: string;
  description: string;
  href: string;
  crop: string;
  imageUrl?: string;
  item?: CatalogItem;
};

const HERO_URL = assetUrl(heroPng);
const PRODUCTS_URL = assetUrl(productsPng);

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'Recreate This Look', href: '/recreate-this-look' },
  { label: 'Budget Finds', href: '/budget-finds' },
  { label: 'Styling Guides', href: '/styling-guides' },
  { label: 'Shop Similar Finds', href: '/shop-similar-finds' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

const fallbackPosts: DisplayPost[] = [
  {
    title: 'Rustic Gold Wedding Table Decor',
    description: 'Warm textures, candlelight, greenery and simple shopping swaps.',
    date: 'May 12, 2024',
    href: '/recreate-this-look',
    tag: 'Recreate this look',
  },
  {
    title: '10 Budget-Friendly Table Decor Ideas That Look Luxe',
    description: 'Simple styling tricks and affordable pieces for a polished tablescape.',
    date: 'May 8, 2024',
    href: '/budget-finds',
    tag: 'Budget finds',
  },
  {
    title: 'How to Style a Wedding Table from Start to Finish',
    description: 'A complete guide to palette, texture, candles and final details.',
    date: 'May 5, 2024',
    href: '/styling-guides',
    tag: 'Styling guides',
  },
  {
    title: 'Neutral Wedding Decor Ideas for a Timeless Look',
    description: 'Why soft neutrals never go out of style and how to use them.',
    date: 'May 1, 2024',
    href: '/shop-similar-finds',
    tag: 'Shop similar finds',
  },
];

const fallbackProducts: Product[] = [
  { title: 'Gold Taper Candlestick Holders', price: 'EUR 42.99', source: 'Amazon', crop: 'crop-left' },
  { title: 'Neutral Linen Table Runner', price: 'EUR 28.99', source: 'Amazon', crop: 'crop-linen' },
  { title: 'Gold Beaded Charger Plates', price: 'EUR 39.99', source: 'Temu', crop: 'crop-plate' },
  { title: 'Glass Footed Vase Bowl', price: 'EUR 34.99', source: 'Amazon', crop: 'crop-floral' },
  { title: 'Linen Cloth Napkins Set', price: 'EUR 24.99', source: 'Amazon', crop: 'crop-linen' },
  { title: 'Mercury Glass Votive Holders', price: 'EUR 22.99', source: 'Temu', crop: 'crop-votive' },
  { title: 'Vintage Style Goblet Glasses', price: 'EUR 32.99', source: 'Amazon', crop: 'crop-glass' },
  { title: 'Eucalyptus Garland', price: 'EUR 18.99', source: 'Temu', crop: 'crop-greenery' },
];

const fallbackGuideCards: GuideCard[] = [
  {
    number: '01',
    title: 'How to Style Ceremony Arches',
    description: 'Design breathtaking arches that frame your vows and elevate the backdrop.',
    href: '/styling-guides',
    crop: 'crop-arch',
  },
  {
    number: '02',
    title: 'Perfect Place Settings',
    description: 'Layer plates, linens and flatware for a polished guest experience.',
    href: '/styling-guides',
    crop: 'crop-place',
  },
  {
    number: '03',
    title: 'Centerpiece Proportions 101',
    description: 'How to choose the right scale for every table and guest count.',
    href: '/styling-guides',
    crop: 'crop-floral',
  },
  {
    number: '04',
    title: 'Candles and Lighting Guide',
    description: 'Create the perfect glow with candle heights and warm ambient light.',
    href: '/styling-guides',
    crop: 'crop-votive',
  },
  {
    number: '05',
    title: 'Neutral Color Palette Ideas',
    description: 'Timeless neutral palettes that work for every season and style.',
    href: '/budget-finds',
    crop: 'crop-linen',
  },
  {
    number: '06',
    title: 'Guest Table Layouts',
    description: 'Smart layouts for round, long and mixed table configurations.',
    href: '/recreate-this-look',
    crop: 'crop-wide',
  },
];

function assetUrl(asset: ImportedAsset) {
  return typeof asset === 'string' ? asset : asset.src;
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function mergeContent(content: SiteRendererProps['content']): LocuslyBlogContent {
  const incoming = asObject(content) as Partial<LocuslyBlogContent>;
  return {
    brand: { ...defaultContent.brand, ...(incoming.brand ?? {}) },
    pt: { ...defaultContent.pt, ...(incoming.pt ?? {}) },
    en: { ...defaultContent.en, ...(incoming.en ?? {}) },
    seo: { ...defaultContent.seo, ...(incoming.seo ?? {}) },
  };
}

function getRuntimeData(content: SiteRendererProps['content']): BlogRuntimeData {
  return asObject(content.__blog) as BlogRuntimeData;
}

function getLocale(routePath: string | undefined, runtime: BlogRuntimeData): 'pt' | 'en' {
  if (runtime.locale === 'en' || runtime.locale === 'pt') return runtime.locale;
  return routePath?.startsWith('/pt') ? 'pt' : 'en';
}

function getPath(routePath: string | undefined) {
  return routePath?.replace(/\/$/, '') || '/';
}

function postHref(post: BlogPost, locale: 'pt' | 'en') {
  return `/${locale}/posts/${post.slug}`;
}

function normalizeSearch(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function formatDate(value: string, locale: 'pt' | 'en') {
  try {
    return new Intl.DateTimeFormat(locale === 'pt' ? 'pt-PT' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(`${value}T00:00:00.000Z`));
  } catch {
    return value;
  }
}

function postsForDisplay(posts: BlogPost[], locale: 'pt' | 'en'): DisplayPost[] {
  if (posts.length === 0) return fallbackPosts;
  return posts.map((post) => ({
    title: post.title,
    description: post.description,
    date: formatDate(post.publishedAt, locale),
    href: postHref(post, locale),
    tag: post.tags[0],
  }));
}

function catalogForKind(
  items: CatalogItem[] | undefined,
  locale: 'pt' | 'en',
  kind: CatalogItem['kind'],
) {
  return (items ?? [])
    .filter((item) => item.locale === locale && item.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

function productsForDisplay(items: CatalogItem[] | undefined, locale: 'pt' | 'en'): Product[] {
  const catalogProducts = catalogForKind(items, locale, 'product');
  if (catalogProducts.length === 0) return fallbackProducts;
  return catalogProducts.map((item, index) => ({
    title: item.title,
    description: item.description,
    price: item.price ?? 'View price',
    source: item.source ?? item.category,
    href: item.affiliateUrl || '/shop-similar-finds',
    imageUrl: item.imageUrl,
    category: item.category,
    isBudgetPick: item.isBudgetPick,
    isFeatured: item.isFeatured,
    crop: ['crop-left', 'crop-linen', 'crop-plate', 'crop-floral', 'crop-votive', 'crop-glass'][index % 6] ?? 'crop-center',
  }));
}

function guidesForDisplay(items: CatalogItem[] | undefined, locale: 'pt' | 'en'): GuideCard[] {
  const guides = catalogForKind(items, locale, 'guide');
  if (guides.length === 0) return fallbackGuideCards;
  return guides.map((item, index) => ({
    number: String(index + 1).padStart(2, '0'),
    title: item.title,
    description: item.description,
    href: `/styling-guides#${item.slug}`,
    crop: ['crop-arch', 'crop-place', 'crop-floral', 'crop-votive', 'crop-linen', 'crop-wide'][index % 6] ?? 'crop-center',
    imageUrl: item.imageUrl,
    steps: item.steps,
  }));
}

function looksForDisplay(items: CatalogItem[] | undefined, locale: 'pt' | 'en'): LookCard[] {
  const looks = catalogForKind(items, locale, 'look');
  if (looks.length === 0) {
    return [
      { number: '01', title: 'Rustic Gold Wedding Table Decor', description: 'Warm textures, candlelight and greenery.', href: '/recreate-this-look', crop: 'crop-table' },
      { number: '02', title: 'White Floral Ceremony Arch', description: 'Timeless, elegant and effortlessly romantic.', href: '/styling-guides', crop: 'crop-floral' },
      { number: '03', title: 'Romantic Candlelit Reception Decor', description: 'Soft light, lush blooms and modern romance.', href: '/budget-finds', crop: 'crop-votive' },
    ];
  }
  return looks.map((item, index) => ({
    number: String(index + 1).padStart(2, '0'),
    title: item.title,
    description: item.description,
    href: '/recreate-this-look',
    crop: ['crop-table', 'crop-floral', 'crop-votive', 'crop-wide'][index % 4] ?? 'crop-center',
    imageUrl: item.imageUrl,
    item,
  }));
}

function ImageFrame({
  image = 'hero',
  crop = 'crop-center',
  alt,
  className = '',
  src,
}: {
  image?: 'hero' | 'products';
  crop?: string;
  alt: string;
  className?: string;
  src?: string;
}) {
  return (
    <span className={`lb-image ${className}`}>
      <img className={crop} src={src || (image === 'hero' ? HERO_URL : PRODUCTS_URL)} alt={alt} />
    </span>
  );
}

function LeafMark() {
  return (
    <span className="lb-leaf" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

function SearchIcon() {
  return (
    <svg className="lb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4.2-4.2" />
    </svg>
  );
}

function Header({ currentPath }: { currentPath: string }) {
  return (
    <header className="lb-header">
      <a className="lb-logo" href="/">
        <LeafMark />
        <span>
          <strong>Locusly</strong>
          <small>Curated decor. Beautiful moments.</small>
        </span>
      </a>
      <nav className="lb-desktop-nav" aria-label="Primary">
        {navItems.map((item) => (
          <a
            className={currentPath === item.href ? 'active' : ''}
            href={item.href}
            key={item.href}
          >
            {item.label}
          </a>
        ))}
      </nav>
      <div className="lb-header-actions">
        <a className="lb-search-link" href="/en/posts" aria-label="Search posts">
          <SearchIcon />
        </a>
        <a className="lb-start" href="/budget-finds">Start Here</a>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="lb-footer">
      <div className="lb-footer-logo">
        <a className="lb-logo" href="/">
          <LeafMark />
          <span>
            <strong>Locusly</strong>
            <small>Curated decor. Beautiful moments.</small>
          </span>
        </a>
        <p>As an Amazon Associate, Locusly earns from qualifying purchases. We may also earn commissions from other affiliate programs.</p>
      </div>
      <nav aria-label="Footer">
        <a href="/">Home</a>
        <a href="/recreate-this-look">Recreate This Look</a>
        <a href="/budget-finds">Budget Finds</a>
        <a href="/styling-guides">Styling Guides</a>
        <a href="/shop-similar-finds">Shop Similar Finds</a>
        <a href="/about">About</a>
      </nav>
      <div className="lb-socials">
        <span>Let's connect</span>
        <div>
          <a href="/contact" aria-label="Instagram">IG</a>
          <a href="/contact" aria-label="Pinterest">P</a>
          <a href="/contact" aria-label="TikTok">TT</a>
          <a href="/contact" aria-label="Email">@</a>
        </div>
      </div>
    </footer>
  );
}

function Hero({
  eyebrow,
  title,
  description,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  return (
    <section className="lb-hero-band">
      <div className="lb-hero-copy">
        {eyebrow ? <span className="lb-eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        <span className="lb-gold-line" />
        <p>{description}</p>
        {(primaryHref || secondaryHref) ? (
          <div className="lb-actions">
            {primaryHref && primaryLabel ? <a className="lb-button" href={primaryHref}>{primaryLabel}</a> : null}
            {secondaryHref && secondaryLabel ? <a className="lb-button lb-button-secondary" href={secondaryHref}>{secondaryLabel}</a> : null}
          </div>
        ) : null}
      </div>
      <ImageFrame alt="Elegant wedding table with candles and cream florals" crop="crop-hero" />
    </section>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="lb-section-title">
      <span />
      <h2>{children}</h2>
      <span />
    </div>
  );
}

function FeaturedInspiration({ looks }: { looks: LookCard[] }) {
  return (
    <section className="lb-section">
      <SectionTitle>Featured Inspiration</SectionTitle>
      <div className="lb-feature-row">
        {looks.slice(0, 3).map((card) => (
          <a className="lb-feature-card" href={card.href} key={card.number}>
            <ImageFrame alt="" crop={card.crop} src={card.imageUrl} />
            <span>{card.number}</span>
            <strong>{card.title}</strong>
            <small>{card.description}</small>
          </a>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="lb-band">
      <SectionTitle>How It Works</SectionTitle>
      <div className="lb-process">
        {[
          ['01', 'Get inspired', 'Explore real event looks, ideas and styling tips.'],
          ['02', 'Shop similar finds', 'Find look-alikes and budget-friendly options.'],
          ['03', 'Style your space', 'Bring it together with simple guidance.'],
        ].map(([number, title, body]) => (
          <div className="lb-process-item" key={number}>
            <span className="lb-round-icon">{number}</span>
            <div>
              <strong>{title}</strong>
              <p>{body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CategoryGrid() {
  const categories = [
    ['Recreate This Look', 'Love a look? Recreate it with similar finds and easy-to-follow tips.', '/recreate-this-look', 'crop-table'],
    ['Budget Finds', 'Beautiful decor does not have to be expensive. Discover affordable alternatives.', '/budget-finds', 'crop-linen'],
    ['Styling Guides', 'Step-by-step guides, expert advice and practical styling notes.', '/styling-guides', 'crop-place'],
  ];
  return (
    <section className="lb-section">
      <SectionTitle>Popular Categories</SectionTitle>
      <div className="lb-category-grid">
        {categories.map(([title, body, href, crop]) => (
          <a className="lb-category-card" href={href} key={title}>
            <ImageFrame alt="" crop={crop} />
            <div>
              <strong>{title}</strong>
              <p>{body}</p>
              <span>Browse {'->'}</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function LatestPosts({ posts }: { posts: DisplayPost[] }) {
  return (
    <section className="lb-section">
      <div className="lb-split-title">
        <h2>Latest Blog Posts</h2>
        <a href="/en/posts">View all posts {'->'}</a>
      </div>
      <div className="lb-post-grid">
        {posts.slice(0, 4).map((post, index) => (
          <a className="lb-post-card" href={post.href} key={`${post.href}-${index}`}>
            <ImageFrame alt="" crop={index % 2 === 0 ? 'crop-table' : 'crop-floral'} />
            <span>{post.date}</span>
            <strong>{post.title}</strong>
            <p>{post.description}</p>
          </a>
        ))}
      </div>
    </section>
  );
}

function ProductGrid({ products, compact = false }: { products: Product[]; compact?: boolean }) {
  return (
    <div className={compact ? 'lb-product-strip' : 'lb-product-grid'}>
      {products.map((product) => (
        <a
          className="lb-product-card"
          href={product.href ?? '/shop-similar-finds'}
          key={product.title}
          rel={product.href?.startsWith('http') ? 'nofollow sponsored noreferrer' : undefined}
          target={product.href?.startsWith('http') ? '_blank' : undefined}
        >
          <ImageFrame image="products" alt="" crop={product.crop} src={product.imageUrl} />
          <strong>{product.title}</strong>
          <span>{product.price}</span>
          <small>{product.source}</small>
          <em>View similar find</em>
        </a>
      ))}
    </div>
  );
}

function NewsletterCard() {
  return (
    <aside className="lb-newsletter">
      <span className="lb-round-icon">@</span>
      <h3>Let's stay inspired</h3>
      <p>Get decor ideas, styling tips and shop-worthy finds delivered straight to your inbox.</p>
      <form onSubmit={(event) => event.preventDefault()}>
        <input type="email" placeholder="Email address" aria-label="Email address" />
        <button type="submit">Sign Me Up</button>
      </form>
      <small>No spam, ever. Unsubscribe anytime.</small>
    </aside>
  );
}

function HomePage({
  copy,
  posts,
  looks,
}: {
  copy: BlogLocalizedContent;
  posts: DisplayPost[];
  looks: LookCard[];
}) {
  return (
    <>
      <Hero
        title={copy.title}
        description={copy.description}
        primaryHref="/budget-finds"
        primaryLabel="Start Exploring"
        secondaryHref="/styling-guides"
        secondaryLabel="How It Works"
      />
      <FeaturedInspiration looks={looks} />
      <HowItWorks />
      <CategoryGrid />
      <LatestPosts posts={posts} />
      <section className="lb-section lb-social-grid">
        <a className="lb-social-card" href="/contact">
          <strong>See it on TikTok?</strong>
          <p>Find quick decor looks and shoppable lists you can actually use.</p>
          <span>Follow on TikTok</span>
        </a>
        <a className="lb-social-card lb-social-card-pin" href="/contact">
          <strong>Pinned it?</strong>
          <p>From Pinterest boards to real-life spaces, curated for you.</p>
          <span>Follow on Pinterest</span>
        </a>
        <NewsletterCard />
      </section>
    </>
  );
}

function RecreateLookPage({
  posts,
  products,
  looks,
}: {
  posts: DisplayPost[];
  products: Product[];
  looks: LookCard[];
}) {
  const look = looks[0]?.item;
  const title = look?.title ?? 'Rustic Gold Wedding Table Decor';
  const description =
    look?.description ??
    'Warm candlelight, soft neutrals and touches of gold come together to create a romantic rustic table setting that feels timeless and inviting.';
  const palette = look?.palette?.length
    ? look.palette
    : ['Sage green', 'Warm neutrals', 'Creamy whites', 'Soft golds'];
  const stylingTips = look?.stylingTips?.length
    ? look.stylingTips
    : [
        'Layer your table with a linen runner, charger plates and cloth napkins.',
        'Mix candle heights using tapers, pillars and votives to create depth.',
        'Keep florals low so guests can easily see across the table.',
        'Add personal touches like place cards and greenery sprigs.',
      ];
  return (
    <>
      <section className="lb-article-hero">
        <div className="lb-breadcrumb">Home / Recreate This Look / {title}</div>
        <div>
          <span className="lb-eyebrow">Recreate this look</span>
          <h1>{title}</h1>
          <div className="lb-author-line">
            <span>By Olivia Hartwell</span>
            <span>May 12, 2024</span>
          </div>
          <p>{description}</p>
        </div>
        <ImageFrame alt={title} crop="crop-hero" src={look?.imageUrl} />
      </section>
      <section className="lb-section">
        <SectionTitle>What Makes This Look Work</SectionTitle>
        <div className="lb-detail-grid">
          {[
            ['Color Palette', `${palette.join(', ')} create an earthy romantic feel.`, 'crop-palette'],
            ['Textures', 'Linen, ceramic, glass and raw wood add depth and tactile warmth.', 'crop-place'],
            ['Lighting', 'Mix tapers, votives and hurricanes for a soft glowing ambience.', 'crop-votive'],
            ['Florals', 'Loose garden roses, eucalyptus and greenery add effortless movement.', 'crop-floral'],
          ].map(([title, body, crop]) => (
            <article className="lb-detail-card" key={title}>
              <ImageFrame image={title === 'Color Palette' ? 'products' : 'hero'} alt="" crop={crop} />
              <strong>{title}</strong>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="lb-section">
        <SectionTitle>Shop The Look: Similar Finds</SectionTitle>
        <ProductGrid products={products} />
      </section>
      <section className="lb-compare">
        <article>
          <h2>Budget Version</h2>
          <p>Beautiful style on a budget.</p>
          <ImageFrame alt="" crop="crop-table" />
          <dl>
            <div><dt>Total Est. Cost</dt><dd>EUR 274</dd></div>
            <div><dt>Perfect For</dt><dd>Intimate gatherings</dd></div>
            <div><dt>Look and Feel</dt><dd>Cozy and inviting</dd></div>
          </dl>
        </article>
        <span>VS.</span>
        <article>
          <h2>More Polished Version</h2>
          <p>Elevated details for an extra wow factor.</p>
          <ImageFrame alt="" crop="crop-floral" />
          <dl>
            <div><dt>Total Est. Cost</dt><dd>EUR 612</dd></div>
            <div><dt>Perfect For</dt><dd>Elegant celebrations</dd></div>
            <div><dt>Look and Feel</dt><dd>Sophisticated and luxe</dd></div>
          </dl>
        </article>
      </section>
      <section className="lb-two-col">
        <div className="lb-tip-card">
          <SectionTitle>Styling Tips</SectionTitle>
          <ol>
            {stylingTips.slice(0, 6).map((tip) => <li key={tip}>{tip}</li>)}
          </ol>
        </div>
        <NewsletterCard />
      </section>
      <LatestPosts posts={posts} />
    </>
  );
}

function BudgetFindsPage({ posts, products }: { posts: DisplayPost[]; products: Product[] }) {
  const [query, setQuery] = useState('');
  const normalized = normalizeSearch(query);
  const filtered = posts.filter((post) =>
    normalizeSearch(`${post.title} ${post.description} ${post.tag ?? ''}`).includes(normalized),
  );

  return (
    <>
      <Hero
        title="Budget Finds That Look Luxe"
        description="Affordable decor ideas that do not look cheap. Handpicked inspiration, smart shopping tips and budget-friendly alternatives that wow."
        primaryHref="#finds"
        primaryLabel="Start Exploring"
        secondaryHref="/styling-guides"
        secondaryLabel="How It Works"
      />
      <section className="lb-tabs" aria-label="Categories">
        {['All Finds', 'Table Decor', 'Ceremony Decor', 'Candles', 'Florals', 'Favors'].map((item, index) => (
          <a className={index === 0 ? 'active' : ''} href="#finds" key={item}>{item}</a>
        ))}
      </section>
      <section className="lb-section lb-budget-layout" id="finds">
        <div>
          <article className="lb-feature-guide">
            <ImageFrame alt="" crop="crop-table" />
            <div>
              <span>Featured</span>
              <h2>How to Create a Luxe Look on a Small Budget</h2>
              <p>Designer-approved tips, affordable swaps and smart styling tricks to elevate your day without overspending.</p>
              <a href="/recreate-this-look">Read the full guide {'->'}</a>
            </div>
          </article>
          <div className="lb-search-panel">
            <SearchIcon />
            <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search decor, styles, or products..." />
          </div>
          <div className="lb-post-grid">
            {filtered.slice(0, 8).map((post, index) => (
              <a className="lb-post-card" href={post.href} key={`${post.href}-${index}`}>
                <ImageFrame alt="" crop={index % 3 === 0 ? 'crop-floral' : index % 3 === 1 ? 'crop-votive' : 'crop-place'} />
                <span>{post.date}</span>
                <strong>{post.title}</strong>
                <p>{post.description}</p>
                <em>Read more {'->'}</em>
              </a>
            ))}
          </div>
        </div>
        <aside className="lb-sidebar">
          <div className="lb-side-card">
            <h3>Budget Styling Tips</h3>
            {['Mix high and low', 'Shop off-season', 'Repurpose smartly', 'Buy in bulk'].map((tip, index) => (
              <p key={tip}><span>{String(index + 1).padStart(2, '0')}</span>{tip}</p>
            ))}
          </div>
          <div className="lb-side-card">
            <h3>Most Saved Posts</h3>
            {posts.slice(0, 4).map((post) => (
              <a href={post.href} key={post.title}>{post.title}</a>
            ))}
          </div>
          <NewsletterCard />
        </aside>
      </section>
      <section className="lb-section">
        <div className="lb-split-title">
          <h2>Under EUR 25 Favorites</h2>
          <a href="/shop-similar-finds">Shop more affordable finds {'->'}</a>
        </div>
        <ProductGrid products={products.filter((product) => product.isBudgetPick !== false)} compact />
      </section>
    </>
  );
}

function StylingGuidesPage({ guides, products }: { guides: GuideCard[]; products: Product[] }) {
  return (
    <>
      <Hero
        title="Styling Guides"
        description="Step-by-step guides and expert tips to help you plan beautiful events with confidence and ease."
        primaryHref="#guides"
        primaryLabel="Browse All Guides"
        secondaryHref="/recreate-this-look"
        secondaryLabel="How It Works"
      />
      <section className="lb-feature-wide">
        <ImageFrame alt="" crop="crop-table" />
        <div>
          <h2>How to Style a Wedding Table from Start to Finish</h2>
          <p>A complete step-by-step guide to designing a stunning wedding table, from choosing your palette to placing the final candle.</p>
          <div className="lb-mini-steps">
            {['Choose a Style', 'Build Your Base', 'Add Layers and Height', 'Finishing Touches'].map((step, index) => (
              <span key={step}><strong>0{index + 1}</strong>{step}</span>
            ))}
          </div>
          <a className="lb-button" href="/recreate-this-look">Read the Full Guide</a>
        </div>
      </section>
      <section className="lb-section" id="guides">
        <SectionTitle>Explore All Styling Guides</SectionTitle>
        <div className="lb-guide-grid">
          {guides.map((guide) => (
            <a className="lb-guide-card" href={guide.href} id={guide.href.split('#')[1]} key={guide.number}>
              <ImageFrame alt="" crop={guide.crop} src={guide.imageUrl} />
              <span>{guide.number}</span>
              <strong>{guide.title}</strong>
              <p>{guide.description}</p>
              <em>Read Guide {'->'}</em>
            </a>
          ))}
        </div>
      </section>
      <section className="lb-band">
        <SectionTitle>Your Styling Checklist</SectionTitle>
        <div className="lb-checklist">
          {['Quantities', 'Colors', 'Materials', 'Timing'].map((item) => (
            <div key={item}>
              <span className="lb-round-icon">+</span>
              <strong>{item}</strong>
              <p>Keep every detail practical, cohesive and stress-free.</p>
            </div>
          ))}
        </div>
      </section>
      <section className="lb-shop-cta">
        <div>
          <h2>Love a look? Shop similar finds.</h2>
          <p>Find decor, styling pieces and budget-friendly alternatives to bring your vision to life.</p>
          <a className="lb-button" href="/shop-similar-finds">Shop Similar Finds</a>
        </div>
        <ProductGrid products={products} compact />
      </section>
    </>
  );
}

function ShopSimilarFindsPage({ products }: { products: Product[] }) {
  const [query, setQuery] = useState('');
  const normalized = normalizeSearch(query);
  const filtered = products.filter((product) =>
    normalizeSearch(`${product.title} ${product.source} ${product.category ?? ''} ${product.description ?? ''}`).includes(normalized),
  );

  return (
    <>
      <Hero
        title="Shop Similar Finds"
        description="Browse curated similar finds inspired by the looks you love. Beautiful pieces, budget-friendly options and timeless style."
      />
      <section className="lb-filter-row">
        <div className="lb-search-panel">
          <SearchIcon />
          <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search decor, styles, or products..." />
        </div>
        <select aria-label="Sort products" defaultValue="featured">
          <option value="featured">Sort: Featured</option>
          <option value="price">Sort: Price</option>
        </select>
      </section>
      <section className="lb-tabs" aria-label="Product categories">
        {['All Finds', 'Table Decor', 'Arches', 'Candles', 'Vases', 'Linens', 'Signage'].map((item, index) => (
          <a className={index === 0 ? 'active' : ''} href="#shop" key={item}>{item}</a>
        ))}
      </section>
      <section className="lb-section" id="shop">
        <div className="lb-product-grid">
          {filtered.map((product) => (
            <a
              className="lb-product-card"
              href={product.href ?? '/shop-similar-finds'}
              key={product.title}
              rel={product.href?.startsWith('http') ? 'nofollow sponsored noreferrer' : undefined}
              target={product.href?.startsWith('http') ? '_blank' : undefined}
            >
              <ImageFrame image="products" alt="" crop={product.crop} src={product.imageUrl} />
              <strong>{product.title}</strong>
              <span>{product.price}</span>
              <small>{product.source}</small>
              <em>View similar find</em>
            </a>
          ))}
        </div>
      </section>
      <section className="lb-compare lb-save">
        <article>
          <h2>Compare and Save</h2>
          <p>We find similar pieces across popular retailers so you can choose the best option for your style and budget.</p>
        </article>
        <article>
          <h2>Amazon finds</h2>
          <ProductGrid products={products.filter((product) => product.source.toLowerCase().includes('amazon')).slice(0, 6)} compact />
        </article>
      </section>
      <section className="lb-disclosure">
        <strong>Affiliate disclosure</strong>
        <p>As an Amazon Associate, Locusly earns from qualifying purchases. We also partner with other affiliate programs.</p>
        <a href="/about">Learn more {'->'}</a>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <Hero
        title="About Locusly"
        description="Locusly is your go-to affiliate blog for beautiful wedding and event decor inspiration. We help you recreate stunning looks with similar finds, styling tips and budget-friendly alternatives you can shop with confidence."
        primaryHref="/budget-finds"
        primaryLabel="Start Exploring"
      />
      <section className="lb-founder">
        <ImageFrame image="products" alt="" crop="crop-floral" />
        <div>
          <span className="lb-eyebrow">Founder and editor</span>
          <h2>Hi, I'm Ava</h2>
          <p>I am a decor lover, researcher and content creator with a passion for helping you bring beautiful ideas to life. After planning my own events and spending hours finding the perfect pieces, I created Locusly to make the process easier.</p>
          <p>Every post is thoughtfully curated and reviewed so you can shop with confidence and create moments that feel truly personal.</p>
          <strong>Ava</strong>
        </div>
      </section>
      <HowItWorks />
      <section className="lb-section">
        <SectionTitle>Our Promise To You</SectionTitle>
        <div className="lb-detail-grid">
          {['Honest Recommendations', 'Curated Similar Finds', 'Budget Options', 'Style-First Content'].map((title) => (
            <article className="lb-promise" key={title}>
              <span className="lb-round-icon">+</span>
              <strong>{title}</strong>
              <p>We focus on practical, beautiful recommendations you can actually use.</p>
            </article>
          ))}
        </div>
      </section>
      <CategoryGrid />
    </>
  );
}

function ContactPage() {
  return (
    <>
      <Hero
        title="Contact"
        description="We love hearing from our readers. Whether you have a question, a collaboration idea or inspiration to share, we are here to help."
      />
      <section className="lb-contact-grid">
        <form className="lb-form-card" onSubmit={(event) => event.preventDefault()}>
          <h2>Send us a message</h2>
          <p>Fill out the form below and we will get back to you as soon as possible.</p>
          <label>Full Name<input placeholder="Your name" /></label>
          <label>Email Address<input type="email" placeholder="you@email.com" /></label>
          <label>Subject<select defaultValue=""><option value="" disabled>How can we help?</option><option>Collaboration</option><option>Affiliate question</option><option>Broken link</option></select></label>
          <label>Message<textarea placeholder="Write your message..." /></label>
          <button className="lb-button" type="submit">Send Message</button>
        </form>
        <form className="lb-form-card" onSubmit={(event) => event.preventDefault()}>
          <h2>Submit inspiration</h2>
          <p>Have a space or theme you would love to see recreated? Send it our way.</p>
          <label>What would you like us to recreate?<select defaultValue=""><option value="" disabled>Select a category or theme</option><option>Wedding table</option><option>Ceremony decor</option><option>Budget find</option></select></label>
          <div className="lb-dropzone">Drag and drop an image here or choose file</div>
          <label>Tell us more<textarea placeholder="Colors, style, budget, or anything else..." /></label>
          <button className="lb-button" type="submit">Submit Inspiration</button>
        </form>
      </section>
      <section className="lb-section lb-social-grid">
        <a className="lb-social-card" href="/contact"><strong>Collaborations</strong><p>Interested in working together? We would love to hear your ideas.</p></a>
        <a className="lb-social-card" href="/about"><strong>Affiliate Questions</strong><p>Learn more about our affiliate disclosures and partnerships.</p></a>
        <NewsletterCard />
      </section>
    </>
  );
}

function ArchivePage({
  copy,
  locale,
  posts,
}: {
  copy: BlogLocalizedContent;
  locale: 'pt' | 'en';
  posts: DisplayPost[];
}) {
  const [query, setQuery] = useState('');
  const normalizedQuery = normalizeSearch(query);
  const filtered = posts.filter((post) =>
    normalizeSearch(`${post.title} ${post.description} ${post.tag ?? ''}`).includes(normalizedQuery),
  );

  return (
    <>
      <Hero
        eyebrow={copy.eyebrow}
        title={copy.allPostsLabel}
        description={copy.description}
      />
      <section className="lb-section">
        <form className="lb-search-panel lb-archive-search" role="search" onSubmit={(event) => event.preventDefault()}>
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={copy.searchPlaceholder}
          />
        </form>
        {filtered.length === 0 ? (
          <p className="lb-empty">{copy.noResults}</p>
        ) : (
          <div className="lb-post-grid">
            {filtered.map((post, index) => (
              <a className="lb-post-card" href={post.href} key={`${post.href}-${index}`}>
                <ImageFrame alt="" crop={index % 2 === 0 ? 'crop-table' : 'crop-floral'} />
                <span>{post.date}</span>
                <strong>{post.title}</strong>
                <p>{post.description}</p>
                <em>{copy.readLabel} {'->'}</em>
              </a>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function renderSection(section: BlogPostSection, index: number) {
  if (section.type === 'h2') return <h2 key={index}>{section.text}</h2>;
  if (section.type === 'h3') return <h3 key={index}>{section.text}</h3>;
  if (section.type === 'lead') return <p className="lb-lead" key={index}>{section.text}</p>;
  if (section.type === 'ul') {
    return <ul key={index}>{section.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>;
  }
  if (section.type === 'ol') {
    return <ol key={index}>{section.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ol>;
  }
  if (section.type === 'quote') {
    return (
      <blockquote key={index}>
        <p>{section.text}</p>
        {section.cite ? <cite>{section.cite}</cite> : null}
      </blockquote>
    );
  }
  if (section.type === 'callout') {
    return (
      <aside className="lb-callout" key={index}>
        <strong>{section.title}</strong>
        <p>{section.text}</p>
      </aside>
    );
  }
  if (section.type === 'divider') return <hr key={index} />;
  return <p key={index}>{section.text}</p>;
}

function ReadingPage({
  copy,
  locale,
  post,
  relatedPosts,
}: {
  copy: BlogLocalizedContent;
  locale: 'pt' | 'en';
  post: BlogPost;
  relatedPosts: DisplayPost[];
}) {
  return (
    <>
      <main className="lb-reading">
        <a className="lb-back" href={`/${locale}/posts`}>{copy.backLabel}</a>
        <article>
          <header className="lb-article-hero lb-reading-hero">
            <div>
              <span className="lb-eyebrow">{post.tags[0] ?? copy.eyebrow}</span>
              <h1>{post.title}</h1>
              <div className="lb-author-line">
                <span>By {post.author.name}</span>
                <span>{formatDate(post.publishedAt, locale)}</span>
                <span>{post.readingTimeMin} min read</span>
              </div>
              <p>{post.description}</p>
            </div>
            <ImageFrame alt="" crop="crop-hero" />
          </header>
          <div className="lb-prose">{post.sections.map(renderSection)}</div>
        </article>
      </main>
      <LatestPosts posts={relatedPosts} />
    </>
  );
}

export default function LocuslyBlogRenderer({ content, routePath }: SiteRendererProps) {
  const data = useMemo(() => mergeContent(content), [content]);
  const runtime = useMemo(() => getRuntimeData(content), [content]);
  const locale = getLocale(routePath, runtime);
  const copy = data[locale];
  const dynamicPosts = runtime.posts ?? [];
  const displayPosts = postsForDisplay(dynamicPosts, locale);
  const catalogItems = runtime.catalogItems ?? [];
  const displayProducts = productsForDisplay(catalogItems, locale);
  const displayGuides = guidesForDisplay(catalogItems, locale);
  const displayLooks = looksForDisplay(catalogItems, locale);
  const currentPath = getPath(routePath);
  const post = runtime.post ?? null;

  let page;
  if (post) page = <ReadingPage copy={copy} locale={locale} post={post} relatedPosts={displayPosts} />;
  else if (currentPath === '/recreate-this-look') page = <RecreateLookPage posts={displayPosts} products={displayProducts} looks={displayLooks} />;
  else if (currentPath === '/budget-finds') page = <BudgetFindsPage posts={displayPosts} products={displayProducts} />;
  else if (currentPath === '/styling-guides') page = <StylingGuidesPage guides={displayGuides} products={displayProducts} />;
  else if (currentPath === '/shop-similar-finds') page = <ShopSimilarFindsPage products={displayProducts} />;
  else if (currentPath === '/about') page = <AboutPage />;
  else if (currentPath === '/contact') page = <ContactPage />;
  else if (currentPath === '/pt/posts' || currentPath === '/en/posts' || currentPath === '/posts') {
    page = <ArchivePage copy={copy} locale={locale} posts={displayPosts} />;
  } else page = <HomePage copy={copy} posts={displayPosts} looks={displayLooks} />;

  return (
    <div className="lb-root">
      <style>{styles}</style>
      <Header currentPath={currentPath} />
      {page}
      <Footer />
    </div>
  );
}

const styles = `
.lb-root {
  --bg: #faf9f6;
  --panel: #fffdfa;
  --soft: #f3eee7;
  --ink: #202326;
  --muted: #62645f;
  --line: #e2d7ca;
  --green: #748158;
  --green-dark: #5f6c46;
  --gold: #c29a55;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.lb-root * {
  box-sizing: border-box;
}
.lb-root a {
  color: inherit;
  text-decoration: none;
}
.lb-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) auto;
  align-items: center;
  gap: 28px;
  min-height: 92px;
  padding: 0 64px;
  border-bottom: 1px solid rgba(226, 215, 202, 0.72);
  background: rgba(255, 253, 250, 0.94);
  backdrop-filter: blur(18px);
}
.lb-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.lb-logo strong {
  display: block;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 43px;
  line-height: 0.92;
  font-weight: 500;
  letter-spacing: 0;
}
.lb-logo small {
  display: block;
  margin-top: 7px;
  color: var(--muted);
  font-size: 8px;
  font-weight: 720;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.lb-leaf {
  position: relative;
  width: 20px;
  height: 40px;
  display: inline-block;
}
.lb-leaf::before {
  position: absolute;
  left: 9px;
  top: 4px;
  width: 1px;
  height: 32px;
  background: var(--gold);
  content: "";
  transform: rotate(-14deg);
}
.lb-leaf span {
  position: absolute;
  width: 10px;
  height: 16px;
  border: 1.5px solid var(--green);
  border-radius: 999px 999px 999px 0;
  transform: rotate(-35deg);
}
.lb-leaf span:nth-child(1) { left: 0; top: 8px; }
.lb-leaf span:nth-child(2) { left: 8px; top: 17px; transform: rotate(145deg); }
.lb-leaf span:nth-child(3) { left: 1px; top: 25px; }
.lb-desktop-nav {
  display: flex;
  justify-content: center;
  gap: 28px;
  font-size: 14px;
  font-weight: 680;
}
.lb-desktop-nav a {
  position: relative;
  padding: 35px 0 32px;
}
.lb-desktop-nav a.active::after,
.lb-desktop-nav a:hover::after {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 23px;
  height: 2px;
  background: var(--gold);
  content: "";
}
.lb-header-actions {
  display: flex;
  align-items: center;
  gap: 18px;
}
.lb-search-link {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
}
.lb-icon {
  width: 22px;
  height: 22px;
}
.lb-start,
.lb-button,
.lb-product-card em,
.lb-social-card span {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: var(--green);
  color: #fff;
  padding: 0 22px;
  font-weight: 760;
  font-style: normal;
  box-shadow: 0 8px 18px rgba(95, 108, 70, 0.18);
}
.lb-button-secondary {
  border: 1px solid #cfc3b5;
  background: rgba(255,255,255,0.65);
  color: var(--ink);
  box-shadow: none;
}
.lb-hero-band {
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
  min-height: 382px;
  background: linear-gradient(90deg, #fbf7f1 0%, #fbf7f1 35%, #f5eee6 100%);
}
.lb-hero-copy {
  align-self: center;
  padding: 56px 0 56px 86px;
}
.lb-eyebrow,
.lb-section-title h2,
.lb-split-title h2 {
  color: var(--green-dark);
  font-size: 13px;
  font-weight: 780;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}
.lb-hero-copy h1,
.lb-article-hero h1 {
  max-width: 700px;
  margin: 12px 0 12px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 72px;
  line-height: 0.98;
  font-weight: 500;
  letter-spacing: 0;
}
.lb-gold-line {
  display: block;
  width: 68px;
  height: 2px;
  margin: 20px 0 24px;
  background: var(--gold);
}
.lb-hero-copy p,
.lb-article-hero p {
  max-width: 520px;
  color: #30342f;
  font-size: 18px;
  line-height: 1.55;
}
.lb-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}
.lb-image {
  display: block;
  overflow: hidden;
  background: var(--soft);
}
.lb-image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.lb-hero-band > .lb-image {
  min-height: 382px;
}
.crop-hero { object-position: 55% 50%; }
.crop-table { object-position: 42% 58%; }
.crop-floral { object-position: 66% 34%; }
.crop-votive { object-position: 48% 48%; }
.crop-place { object-position: 78% 68%; }
.crop-wide { object-position: 50% 50%; }
.crop-left { object-position: 10% 44%; }
.crop-linen { object-position: 28% 82%; }
.crop-plate { object-position: 50% 74%; }
.crop-glass { object-position: 82% 60%; }
.crop-greenery { object-position: 25% 55%; }
.crop-palette { object-position: 55% 75%; }
.crop-arch { object-position: 62% 35%; }
.crop-center { object-position: center; }
.lb-section,
.lb-feature-wide,
.lb-article-hero,
.lb-two-col,
.lb-compare,
.lb-shop-cta,
.lb-founder,
.lb-contact-grid,
.lb-filter-row,
.lb-tabs,
.lb-disclosure,
.lb-reading {
  width: min(1240px, calc(100% - 72px));
  margin: 0 auto;
}
.lb-section {
  padding: 42px 0;
}
.lb-section-title {
  display: grid;
  grid-template-columns: minmax(40px, 1fr) auto minmax(40px, 1fr);
  align-items: center;
  gap: 18px;
  margin-bottom: 28px;
  text-align: center;
}
.lb-section-title span {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
}
.lb-section-title h2,
.lb-split-title h2 {
  margin: 0;
  color: var(--ink);
}
.lb-feature-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}
.lb-feature-card,
.lb-post-card,
.lb-category-card,
.lb-detail-card,
.lb-product-card,
.lb-guide-card,
.lb-social-card,
.lb-newsletter,
.lb-side-card,
.lb-feature-guide,
.lb-form-card,
.lb-promise {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 253, 250, 0.92);
  box-shadow: 0 10px 30px rgba(91, 71, 45, 0.06);
}
.lb-feature-card {
  position: relative;
  min-height: 232px;
  padding: 0 0 22px;
  overflow: hidden;
  color: #fff;
}
.lb-feature-card .lb-image {
  height: 232px;
}
.lb-feature-card::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 35%, rgba(0,0,0,0.72));
  content: "";
}
.lb-feature-card > span,
.lb-feature-card > strong,
.lb-feature-card > small {
  position: absolute;
  z-index: 1;
}
.lb-feature-card > span {
  left: 22px;
  bottom: 42px;
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255,255,255,0.92);
  color: var(--gold);
  font-weight: 760;
}
.lb-feature-card > strong {
  left: 84px;
  right: 20px;
  bottom: 50px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 22px;
  font-weight: 500;
}
.lb-feature-card > small {
  left: 84px;
  right: 20px;
  bottom: 25px;
  color: rgba(255,255,255,0.84);
  font-size: 13px;
}
.lb-band {
  margin: 28px 0;
  padding: 34px 0;
  background: linear-gradient(90deg, #faf7f2, #f0eadf, #faf7f2);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.lb-process,
.lb-checklist {
  width: min(1120px, calc(100% - 72px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}
.lb-process-item,
.lb-checklist div {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
}
.lb-round-icon {
  display: inline-flex;
  width: 62px;
  height: 62px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  font-weight: 760;
}
.lb-process strong,
.lb-checklist strong,
.lb-category-card strong,
.lb-post-card strong,
.lb-detail-card strong,
.lb-product-card strong,
.lb-guide-card strong,
.lb-social-card strong,
.lb-newsletter h3,
.lb-side-card h3,
.lb-form-card h2,
.lb-founder h2,
.lb-promise strong {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 24px;
  line-height: 1.15;
  font-weight: 500;
}
.lb-process p,
.lb-checklist p,
.lb-category-card p,
.lb-post-card p,
.lb-detail-card p,
.lb-social-card p,
.lb-newsletter p,
.lb-side-card p,
.lb-form-card p,
.lb-founder p,
.lb-promise p,
.lb-disclosure p {
  color: var(--muted);
  line-height: 1.5;
}
.lb-category-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}
.lb-category-card {
  display: grid;
  grid-template-columns: 42% minmax(0, 1fr);
  overflow: hidden;
}
.lb-category-card .lb-image {
  min-height: 160px;
}
.lb-category-card div {
  padding: 24px;
}
.lb-category-card span,
.lb-split-title a,
.lb-feature-guide a,
.lb-post-card em,
.lb-guide-card em,
.lb-disclosure a {
  color: var(--green-dark);
  font-style: normal;
  font-weight: 760;
}
.lb-split-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}
.lb-post-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}
.lb-post-card {
  overflow: hidden;
}
.lb-post-card .lb-image {
  aspect-ratio: 16 / 8.5;
}
.lb-post-card span,
.lb-author-line,
.lb-breadcrumb {
  color: var(--muted);
  font-size: 12px;
  font-weight: 680;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.lb-post-card strong,
.lb-post-card p,
.lb-post-card span,
.lb-post-card em {
  display: block;
  margin: 0 18px 12px;
}
.lb-post-card span {
  margin-top: 14px;
}
.lb-social-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}
.lb-social-card,
.lb-newsletter {
  min-height: 188px;
  padding: 28px;
  background:
    linear-gradient(90deg, rgba(255,253,250,0.9), rgba(255,253,250,0.68)),
    var(--soft);
}
.lb-social-card span {
  margin-top: 18px;
  width: max-content;
}
.lb-social-card-pin {
  background:
    linear-gradient(90deg, rgba(255,253,250,0.9), rgba(255,253,250,0.72)),
    color-mix(in srgb, var(--green) 14%, white);
}
.lb-newsletter form {
  display: flex;
  gap: 8px;
  margin: 18px 0 8px;
}
.lb-newsletter input,
.lb-form-card input,
.lb-form-card select,
.lb-form-card textarea,
.lb-search-panel input,
.lb-filter-row select {
  width: 100%;
  min-height: 44px;
  border: 1px solid #d9cfc3;
  border-radius: 6px;
  background: #fff;
  color: var(--ink);
  padding: 0 14px;
  font: inherit;
}
.lb-newsletter button {
  min-height: 44px;
  border: 0;
  border-radius: 6px;
  background: var(--green);
  color: #fff;
  padding: 0 18px;
  font-weight: 760;
  white-space: nowrap;
}
.lb-footer {
  display: grid;
  grid-template-columns: 1.1fr 1fr auto;
  gap: 36px;
  padding: 40px 64px 26px;
  border-top: 1px solid var(--line);
  background: #f4efe7;
}
.lb-footer .lb-logo strong {
  font-size: 38px;
}
.lb-footer p {
  max-width: 440px;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
}
.lb-footer nav {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px 40px;
  font-size: 13px;
}
.lb-socials {
  display: grid;
  align-content: start;
  gap: 12px;
  color: var(--muted);
}
.lb-socials div {
  display: flex;
  gap: 9px;
}
.lb-socials a {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d4c8ba;
  border-radius: 50%;
  background: #fff;
  font-size: 11px;
  font-weight: 760;
}
.lb-article-hero {
  display: grid;
  grid-template-columns: minmax(0, 0.72fr) minmax(0, 1fr);
  gap: 46px;
  align-items: center;
  padding: 30px 0 24px;
}
.lb-article-hero .lb-image {
  height: 350px;
  border-radius: 8px;
}
.lb-article-hero h1 {
  font-size: 58px;
}
.lb-author-line {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 18px 0;
}
.lb-detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}
.lb-detail-card {
  overflow: hidden;
  text-align: center;
}
.lb-detail-card .lb-image {
  height: 112px;
}
.lb-detail-card strong,
.lb-detail-card p {
  display: block;
  padding: 0 18px;
}
.lb-product-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}
.lb-product-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
.lb-product-card {
  padding: 0 0 16px;
  overflow: hidden;
}
.lb-product-card .lb-image {
  height: 150px;
}
.lb-product-card strong,
.lb-product-card span,
.lb-product-card small,
.lb-product-card em {
  display: block;
  margin: 10px 14px 0;
}
.lb-product-card strong {
  font-size: 17px;
}
.lb-product-card span {
  font-weight: 760;
}
.lb-product-card small {
  color: var(--muted);
}
.lb-product-card em {
  min-height: 34px;
  margin-top: 14px;
}
.lb-compare {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 28px;
  align-items: stretch;
  margin-top: 10px;
  margin-bottom: 30px;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel);
}
.lb-compare > span {
  align-self: center;
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--gold);
  border-radius: 50%;
  color: var(--gold);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 22px;
}
.lb-compare article .lb-image {
  height: 118px;
  border-radius: 8px;
}
.lb-compare h2,
.lb-feature-guide h2,
.lb-feature-wide h2,
.lb-shop-cta h2 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 34px;
  font-weight: 500;
}
.lb-compare dl {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.lb-compare dt {
  color: var(--muted);
  font-size: 12px;
  font-weight: 760;
}
.lb-compare dd {
  margin: 4px 0 0;
}
.lb-two-col,
.lb-contact-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 34px;
  padding: 26px 0;
}
.lb-tip-card {
  border-radius: 8px;
  background: var(--soft);
  padding: 24px 34px;
}
.lb-tip-card ol {
  display: grid;
  gap: 14px;
  margin: 0;
  padding-left: 22px;
}
.lb-tabs {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0;
  margin-top: 22px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--panel);
  overflow: hidden;
}
.lb-tabs a {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--line);
  font-weight: 720;
}
.lb-tabs a:last-child {
  border-right: 0;
}
.lb-tabs a.active {
  background: var(--green);
  color: #fff;
}
.lb-budget-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 34px;
}
.lb-feature-guide {
  display: grid;
  grid-template-columns: 44% minmax(0, 1fr);
  gap: 26px;
  padding: 16px;
}
.lb-feature-guide .lb-image {
  min-height: 250px;
  border-radius: 8px;
}
.lb-feature-guide span {
  display: inline-flex;
  border-radius: 4px;
  background: #ece8d6;
  color: var(--green-dark);
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 780;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.lb-search-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px 0;
}
.lb-search-panel input {
  min-height: 52px;
}
.lb-sidebar {
  display: grid;
  gap: 18px;
  align-content: start;
}
.lb-side-card {
  padding: 24px;
}
.lb-side-card h3 {
  margin-top: 0;
  font-size: 18px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}
.lb-side-card p {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
}
.lb-side-card p span {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  font-weight: 760;
}
.lb-side-card a {
  display: block;
  margin-top: 14px;
}
.lb-feature-wide {
  display: grid;
  grid-template-columns: minmax(0, 0.7fr) minmax(0, 1fr);
  gap: 48px;
  align-items: center;
  margin-top: -34px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255,253,250,0.96);
  padding: 16px;
  box-shadow: 0 12px 34px rgba(91, 71, 45, 0.08);
}
.lb-feature-wide .lb-image {
  height: 260px;
  border-radius: 8px;
}
.lb-mini-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 24px 0;
}
.lb-mini-steps span {
  border-top: 1px solid var(--line);
  padding-top: 10px;
  color: var(--muted);
  font-size: 13px;
}
.lb-mini-steps strong {
  display: block;
  color: var(--green-dark);
}
.lb-guide-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}
.lb-guide-card {
  position: relative;
  overflow: hidden;
}
.lb-guide-card .lb-image {
  height: 160px;
}
.lb-guide-card > span {
  position: absolute;
  top: 16px;
  left: 16px;
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff;
  color: var(--gold);
  font-weight: 760;
}
.lb-guide-card strong,
.lb-guide-card p,
.lb-guide-card em {
  display: block;
  margin: 12px 24px;
}
.lb-checklist {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.lb-shop-cta {
  display: grid;
  grid-template-columns: minmax(280px, 0.7fr) minmax(0, 1fr);
  gap: 28px;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 40px;
  padding: 22px;
  border-radius: 8px;
  background: var(--soft);
}
.lb-filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 18px;
  margin-top: 24px;
}
.lb-filter-row .lb-search-panel {
  margin: 0;
}
.lb-save {
  grid-template-columns: minmax(220px, 0.45fr) minmax(0, 1fr);
}
.lb-save .lb-product-strip {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.lb-disclosure {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  margin-bottom: 36px;
  padding: 16px 24px;
  border-radius: 8px;
  background: #eef0e5;
}
.lb-founder {
  display: grid;
  grid-template-columns: minmax(0, 0.7fr) minmax(0, 1fr);
  gap: 60px;
  align-items: center;
  padding: 48px 0;
}
.lb-founder .lb-image {
  height: 320px;
  border-radius: 8px;
}
.lb-founder h2 {
  font-size: 42px;
  margin: 12px 0;
}
.lb-promise {
  padding: 28px;
  min-height: 220px;
}
.lb-contact-grid {
  padding: 42px 0;
}
.lb-form-card {
  padding: 34px;
}
.lb-form-card label {
  display: grid;
  gap: 8px;
  margin-top: 18px;
  font-weight: 720;
}
.lb-form-card textarea {
  min-height: 116px;
  padding-top: 12px;
}
.lb-dropzone {
  display: grid;
  min-height: 156px;
  place-items: center;
  border: 1px dashed #c9a77b;
  border-radius: 8px;
  color: var(--muted);
  text-align: center;
}
.lb-reading {
  padding: 24px 0 54px;
}
.lb-back {
  display: inline-flex;
  margin-bottom: 18px;
  color: var(--green-dark);
  font-weight: 760;
}
.lb-reading-hero {
  width: 100%;
}
.lb-prose {
  width: min(760px, 100%);
  margin: 34px auto 0;
  color: #2f2f2b;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 21px;
  line-height: 1.78;
}
.lb-prose h2,
.lb-prose h3 {
  margin: 42px 0 14px;
  color: var(--ink);
  line-height: 1.16;
}
.lb-prose h2 {
  font-size: 34px;
}
.lb-prose h3 {
  font-size: 25px;
}
.lb-lead {
  color: var(--muted);
  font-size: 24px;
}
.lb-prose blockquote,
.lb-callout {
  margin: 28px 0;
  border-left: 4px solid var(--gold);
  padding: 6px 0 6px 20px;
  color: var(--muted);
}
.lb-prose hr {
  border: 0;
  border-top: 1px solid var(--line);
  margin: 34px 0;
}
.lb-empty {
  padding: 34px;
  border-radius: 8px;
  background: var(--soft);
  color: var(--muted);
}
@media (max-width: 1100px) {
  .lb-header {
    grid-template-columns: 1fr auto;
    padding: 0 28px;
  }
  .lb-desktop-nav {
    grid-column: 1 / -1;
    order: 3;
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 10px;
  }
  .lb-desktop-nav a {
    padding: 10px 0 14px;
    white-space: nowrap;
  }
  .lb-desktop-nav a.active::after,
  .lb-desktop-nav a:hover::after {
    bottom: 6px;
  }
  .lb-hero-band,
  .lb-article-hero,
  .lb-feature-wide,
  .lb-founder {
    grid-template-columns: 1fr;
  }
  .lb-hero-copy {
    padding: 42px 34px 24px;
  }
  .lb-hero-band > .lb-image {
    min-height: 300px;
  }
  .lb-detail-grid,
  .lb-post-grid,
  .lb-product-grid,
  .lb-product-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .lb-budget-layout,
  .lb-shop-cta {
    grid-template-columns: 1fr;
  }
  .lb-sidebar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 720px) {
  .lb-header {
    min-height: auto;
    gap: 14px;
    padding: 16px;
  }
  .lb-logo strong {
    font-size: 34px;
  }
  .lb-start {
    min-height: 36px;
    padding: 0 12px;
    font-size: 12px;
  }
  .lb-section,
  .lb-feature-wide,
  .lb-article-hero,
  .lb-two-col,
  .lb-compare,
  .lb-shop-cta,
  .lb-founder,
  .lb-contact-grid,
  .lb-filter-row,
  .lb-tabs,
  .lb-disclosure,
  .lb-reading {
    width: min(100% - 28px, 1240px);
  }
  .lb-hero-copy h1,
  .lb-article-hero h1 {
    font-size: 44px;
  }
  .lb-hero-copy p,
  .lb-article-hero p {
    font-size: 16px;
  }
  .lb-feature-row,
  .lb-category-grid,
  .lb-social-grid,
  .lb-detail-grid,
  .lb-post-grid,
  .lb-product-grid,
  .lb-product-strip,
  .lb-guide-grid,
  .lb-process,
  .lb-checklist,
  .lb-two-col,
  .lb-contact-grid,
  .lb-sidebar,
  .lb-footer,
  .lb-mini-steps,
  .lb-filter-row,
  .lb-save,
  .lb-save .lb-product-strip {
    grid-template-columns: 1fr;
  }
  .lb-category-card,
  .lb-feature-guide,
  .lb-compare,
  .lb-shop-cta,
  .lb-disclosure {
    grid-template-columns: 1fr;
  }
  .lb-tabs {
    display: flex;
    overflow-x: auto;
    border-radius: 8px;
  }
  .lb-tabs a {
    min-width: 150px;
  }
  .lb-footer {
    padding: 30px 18px;
  }
}
`;
